<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import agents from '../../../../api/agents';
import Button from '../../../../components-next/button/Button.vue';

const props = defineProps({
  currentStage: {
    type: String,
    default: '',
  },
});

// eslint-disable-next-line vue/custom-event-name-casing -- consumers use @items-created (kebab-case in template); rename would cascade to PipelineHeader.vue
const emit = defineEmits(['close', 'items-created']);
const { t } = useI18n();
const store = useStore();
const { showAlert } = useAlert();

const loading = ref(false);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const itemsList = ref([]);
const agentsList = ref([]);
const loadingAgents = ref(false);

// Form para novo item
const newItem = ref({
  title: '',
  priority: 'none',
  agent_id: '',
});

// Prioridades disponíveis
const priorityOptions = [
  { id: 'none', name: t('PIPELINE.PRIORITY_LABELS.NONE') },
  { id: 'low', name: t('PIPELINE.PRIORITY_LABELS.LOW') },
  { id: 'medium', name: t('PIPELINE.PRIORITY_LABELS.MEDIUM') },
  { id: 'high', name: t('PIPELINE.PRIORITY_LABELS.HIGH') },
  { id: 'urgent', name: t('PIPELINE.PRIORITY_LABELS.URGENT') },
];

const stages = computed(() => {
  if (!selectedPipeline.value?.stages) return [];

  return Object.entries(selectedPipeline.value.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});

const selectedStage = ref(props.currentStage || '');

// Função para carregar os agentes
const fetchAgents = async () => {
  try {
    loadingAgents.value = true;
    const { data } = await agents.get();
    agentsList.value = data;
  } catch (error) {
    // Error handled silently
  } finally {
    loadingAgents.value = false;
  }
};

const addItemToList = () => {
  if (!newItem.value.title.trim()) return;

  itemsList.value.push({
    ...newItem.value,
    id: Date.now(), // ID temporário para a lista
  });

  // Limpa o form
  newItem.value = {
    title: '',
    priority: 'none',
    agent_id: '',
  };
};

const removeItemFromList = itemId => {
  itemsList.value = itemsList.value.filter(item => item.id !== itemId);
};

const handleSubmit = async () => {
  if (!itemsList.value.length || !selectedStage.value) return;

  try {
    loading.value = true;
    const items = itemsList.value.map((item, index) => ({
      pipeline_id: Number(selectedPipeline.value.id),
      pipeline_stage: selectedStage.value,
      position: index + 1,
      item_details: {
        title: item.title,
        priority: item.priority,
        agent_id: item.agent_id || null,
      },
    }));

    const createdItems = await Promise.all(
      items.map(item => PipelineCardsAPI.createItem(item))
    );

    // Atualiza o store com os novos itens
    await store.dispatch('pipelineCards/itemUpdated');

    /* eslint-disable vue/custom-event-name-casing */
    emit(
      'items-created',
      createdItems.map(response => response.data)
    );
    /* eslint-enable vue/custom-event-name-casing */
    emit('close');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[BulkAddModal] handleSubmit error:', error);
    showAlert(t('PIPELINE.MESSAGES.BULK_CREATE_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchAgents();
});
</script>

<template>
  <div class="w-full max-w-[800px]">
    <div class="p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.ADD_ITEM') }}
      </h3>

      <div class="mb-4">
        <label class="block text-label text-n-slate-12 mb-2">
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.LABEL') }}
        </label>
        <select v-model="selectedStage" class="h-10 w-full">
          <option value="" disabled>
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.PLACEHOLDER') }}
          </option>
          <option v-for="stage in stages" :key="stage.id" :value="stage.id">
            {{ stage.name }}
          </option>
        </select>
      </div>

      <!-- Formulário de novo item -->
      <div class="grid grid-cols-3 gap-4 mb-4">
        <div class="col-span-3">
          <label class="block text-label text-n-slate-12 mb-2">
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.TITLE.LABEL') }}
          </label>
          <input
            v-model="newItem.title"
            type="text"
            class="h-10 w-full"
            :placeholder="
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.TITLE.PLACEHOLDER')
            "
          />
        </div>

        <div>
          <label class="block text-label text-n-slate-12 mb-2">
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.PRIORITY.LABEL') }}
          </label>
          <select v-model="newItem.priority" class="h-10 w-full">
            <option
              v-for="option in priorityOptions"
              :key="option.id"
              :value="option.id"
            >
              {{ option.name }}
            </option>
          </select>
        </div>

        <div class="col-span-2">
          <label class="block text-label text-n-slate-12 mb-2">
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.AGENT.LABEL') }}
          </label>
          <select
            v-model="newItem.agent_id"
            class="h-10 w-full"
            :disabled="loadingAgents"
          >
            <option value="">
              {{
                t('PIPELINE.BULK_ACTIONS.BULK_FORM.INPUTS.AGENT.PLACEHOLDER')
              }}
            </option>
            <option
              v-for="agent in agentsList"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.name }}
            </option>
          </select>
        </div>

        <div class="col-span-3">
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :disabled="!newItem.title.trim()"
            @click="addItemToList"
          >
            {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.ADD_TO_LIST') }}
          </Button>
        </div>
      </div>

      <!-- Lista de itens -->
      <div
        v-if="itemsList.length"
        class="mb-4 border border-n-weak rounded-lg overflow-hidden max-h-[300px] overflow-y-auto"
      >
        <div
          v-for="(item, index) in itemsList"
          :key="item.id"
          class="flex items-center justify-between p-3 hover:bg-n-alpha-1"
          :class="index < itemsList.length - 1 && 'border-b border-n-weak'"
        >
          <div class="flex items-center gap-4">
            <span class="text-body-main text-n-slate-12">{{ item.title }}</span>
            <span
              class="px-2 py-1 text-label-small font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ t(`PIPELINE.PRIORITY_LABELS.${item.priority.toUpperCase()}`) }}
            </span>
            <span v-if="item.agent_id" class="text-label text-n-slate-11">
              {{ agentsList.find(a => a.id === item.agent_id)?.name }}
            </span>
          </div>
          <Button
            variant="ghost"
            color="ruby"
            size="sm"
            @click="removeItemFromList(item.id)"
          >
            <template #icon>
              <fluent-icon icon="delete" size="16" />
            </template>
          </Button>
        </div>
      </div>

      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="$emit('close')">
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :is-loading="loading"
          :disabled="!itemsList.length || !selectedStage"
          @click="handleSubmit"
        >
          {{ t('PIPELINE.BULK_ACTIONS.BULK_FORM.ACTIONS.CONFIRM') }}
        </Button>
      </div>
    </div>
  </div>
</template>
